import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserProjectId } from 'core/userContext';
import { SMSContentDefinition } from 'modules/graphql/types';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { getDefaultLinkShorteningDomain } from 'modules/mailDomains/selectors';

import {
  SMSLinkShorteningContext,
  SMSLinkShorteningContextState,
} from './SMSLinkShorteningContext';
import { TemplateAndContentFormDataLocale } from '../../common/types';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN } from '../constants';
import { useShortLinksLength } from 'modules/templateEditor/hooks/useShortLinksLength';

export const getUseSMSShorteningKey = (projectId: number | string) => {
  return `useSMSLinkShortening-proj-${projectId}`;
};

export const getSMSLinkShorteningDomainKey = (projectId: number | string) => {
  return `SMSLinkShorteningDomain-proj-${projectId}`;
};

/**
 * Sets/gets value for local storage of link tracking and domains
 * Used for links section fields in settings
 */
export const SMSLinkShorteningContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { initialTemplate, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const initialSmsData = (initialTemplate?.mediumData || {}) as SMSContentDefinition;
  const initialData = (initialTemplate || {}) as TemplateAndContentFormDataLocale;
  const isNewTemplate = !initialData.name?.length;
  const projectId = useSelector(selectUserProjectId);
  const defaultLinkShorteningDomain = useSelector(getDefaultLinkShorteningDomain);

  const [isLinkShorteningLocallyEnabled, setIsLinkShorteningLocallyEnabled] =
    useLocalStorage<boolean>(getUseSMSShorteningKey(projectId), false);
  const [localStorageDomain, setLocalStorageDomain] = useLocalStorage<string>(
    getSMSLinkShorteningDomainKey(projectId),
    '',
  );
  /** Determines the status of the checkbox for shortening and tracking links in SMS */
  const [isSMSLinkShorteningEnabled, setIsSMSLinkShorteningEnabled] = useState<boolean>(
    !!smsData.trackingDomain || !!(isNewTemplate && isLinkShorteningLocallyEnabled),
  );

  const defaultSmsLinkShorteningDomain =
    defaultLinkShorteningDomain ?? DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN;

  /** To sync Shorten and track data from local storage, we need to update both */
  const updateIsSMSLinkShorteningEnabledState = (value: boolean) => {
    if (!value) {
      setCurrentMediumField('linkParams', null);
    }
    setIsSMSLinkShorteningEnabled(value);
    setIsLinkShorteningLocallyEnabled(value);
  };

  const handleSetIsSMSLinkShorteningEnabled = () => {
    const newValue = !isSMSLinkShorteningEnabled;
    updateIsSMSLinkShorteningEnabledState(newValue);
    const { trackingDomain } = smsData;
    if (!newValue) {
      setCurrentMediumField('trackingDomain', null);
    } else if (!trackingDomain) {
      setCurrentMediumField('trackingDomain', defaultSmsLinkShorteningDomain);
      setLocalStorageDomain(defaultSmsLinkShorteningDomain);
    }
  };

  const handleSetLocalStorageDomain = (newDomains: string[]) => {
    setCurrentMediumField('trackingDomain', newDomains[0]);
    setLocalStorageDomain(newDomains[0] as string);
  };

  const handleResetIsSMSLinkShorteningEnabled = () => {
    updateIsSMSLinkShorteningEnabledState(
      !!initialSmsData.trackingDomain || !!(isNewTemplate && isLinkShorteningLocallyEnabled),
    );
  };

  const shortlinkLength = useShortLinksLength(initialTemplate);

  /** Handles case where we receive the template data after this form section loads */
  useEffect(() => {
    if (!isSMSLinkShorteningEnabled && !!smsData.trackingDomain !== isSMSLinkShorteningEnabled) {
      updateIsSMSLinkShorteningEnabledState(!!smsData.trackingDomain);
    }
  }, [smsData]);

  const context: SMSLinkShorteningContextState = {
    isSMSLinkShorteningEnabled,
    localStorageDomain,
    resetIsSMSLinkShorteningEnabled: handleResetIsSMSLinkShorteningEnabled,
    setIsSMSLinkShorteningEnabled: handleSetIsSMSLinkShorteningEnabled,
    setLocalStorageDomain: handleSetLocalStorageDomain,
    shortlinkLength,
    defaultSmsLinkShorteningDomain,
  };

  return (
    <SMSLinkShorteningContext.Provider value={context}>
      {children}
    </SMSLinkShorteningContext.Provider>
  );
};
